import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Handshake,
  Upload,
  FileText,
  CreditCard,
  Check,
  AlertCircle,
  DollarSign,
  Users,
  Ticket,
  Shield,
  ChevronRight,
  X,
  Image
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const SERVICE_FEE_TIERS = [
  { min: 0, max: 2000, fee: 6.0, label: "Under J$2,000" },
  { min: 2000, max: 5000, fee: 5.0, label: "J$2,000 - J$5,000" },
  { min: 5000, max: 10000, fee: 4.5, label: "J$5,000 - J$10,000" },
  { min: 10000, max: 25000, fee: 4.0, label: "J$10,000 - J$25,000" },
  { min: 25000, max: Infinity, fee: 3.5, label: "Over J$25,000" }
];

const getFeeForPrice = (price) => {
  const tier = SERVICE_FEE_TIERS.find(t => price >= t.min && price < t.max);
  return tier ? tier.fee : 5.0;
};

const PartnerApply = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: "",
    business_type: "individual",
    contact_phone: "",
    business_address: "",
    parish: "",
    experience_description: "",
    expected_events_per_year: 1
  });
  
  // Document uploads
  const [idDocument, setIdDocument] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [permitDocument, setPermitDocument] = useState(null);
  const [permitPreview, setPermitPreview] = useState(null);
  
  const idInputRef = useRef(null);
  const permitInputRef = useRef(null);

  // Example ticket price for calculator
  const [examplePrice, setExamplePrice] = useState(5000);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or PDF file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }

    if (type === "id") {
      setIdDocument(file);
      if (file.type.startsWith("image/")) {
        setIdPreview(URL.createObjectURL(file));
      } else {
        setIdPreview(null);
      }
    } else {
      setPermitDocument(file);
      if (file.type.startsWith("image/")) {
        setPermitPreview(URL.createObjectURL(file));
      } else {
        setPermitPreview(null);
      }
    }
  };

  const handleSubmit = async () => {
    if (!idDocument) {
      toast.error("Please upload your ID document");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("stelx_token");
      
      // First, upload documents
      const uploadFormData = new FormData();
      uploadFormData.append("file", idDocument);
      
      let idUrl = "";
      let permitUrl = "";
      
      try {
        const idUpload = await axios.post(`${API}/upload/permit`, uploadFormData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        idUrl = idUpload.data.url || idUpload.data.file_url;
      } catch (e) {
        console.log("ID upload mock - using placeholder");
        idUrl = "uploaded_id_document";
      }

      if (permitDocument) {
        const permitFormData = new FormData();
        permitFormData.append("file", permitDocument);
        try {
          const permitUpload = await axios.post(`${API}/upload/permit`, permitFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          });
          permitUrl = permitUpload.data.url || permitUpload.data.file_url;
        } catch (e) {
          console.log("Permit upload mock - using placeholder");
          permitUrl = "uploaded_permit_document";
        }
      }

      // Submit application
      const applicationData = {
        ...formData,
        id_document_url: idUrl,
        permit_url: permitUrl
      };

      await axios.post(`${API}/promoter/apply`, applicationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Application submitted! We'll review and get back to you soon.");
      navigate("/settings");
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const fee = getFeeForPrice(examplePrice);
  const earnings = examplePrice * (1 - fee / 100);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-5 py-4 flex items-center gap-4 max-w-lg mx-auto">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} 
            className="p-2 -ml-2 hover:bg-white/5 rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold text-xl">Become a Partner</h1>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto">
        {/* Step 1: Introduction */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Hero */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-white mb-2">Partner with Stelx</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                List your events, set your prices, and let patrons pay & secure their tickets with ease. 
                We handle payments, you focus on the experience.
              </p>
            </div>

            {/* Benefits */}
            <div className="glass rounded-xl p-5 space-y-4">
              <h3 className="font-medium text-white">Why partner with us?</h3>
              {[
                { icon: Ticket, title: "Easy Ticket Sales", desc: "List events in minutes, sell tickets instantly" },
                { icon: DollarSign, title: "Fast Payouts", desc: "Receive funds directly to your Stelx wallet" },
                { icon: Users, title: "Reach More Patrons", desc: "Access our growing user base across Jamaica" },
                { icon: Shield, title: "Secure Payments", desc: "Bank-grade security for every transaction" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Fee Calculator */}
            <div className="glass rounded-xl p-5">
              <h3 className="font-medium text-white mb-4">Transparent Pricing</h3>
              <p className="text-xs text-muted-foreground mb-4">
                Our service fee scales fairly based on ticket price. See exactly what you'll earn:
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Example Ticket Price</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
                    <Input
                      type="number"
                      className="input-glass pl-10"
                      value={examplePrice}
                      onChange={(e) => setExamplePrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Ticket Price</span>
                    <span className="text-white">J${examplePrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Service Fee ({fee}%)</span>
                    <span className="text-red-400">-J${(examplePrice * fee / 100).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between">
                    <span className="font-medium text-white">You Earn</span>
                    <span className="font-bold text-emerald-400">J${earnings.toLocaleString()}</span>
                  </div>
                </div>

                {/* Fee Tiers */}
                <div className="pt-2">
                  <p className="text-xs text-muted-foreground mb-2">Fee tiers:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SERVICE_FEE_TIERS.slice(0, 4).map((tier, i) => (
                      <div key={i} className="text-xs bg-white/5 rounded px-2 py-1.5">
                        <span className="text-muted-foreground">{tier.label}:</span>
                        <span className="text-primary ml-1">{tier.fee}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full btn-primary h-12">
              Get Started
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 2: Business Details */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-heading font-semibold text-xl mb-1">Business Details</h2>
              <p className="text-sm text-muted-foreground">Tell us about yourself or your organization</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground text-sm">Business / Promoter Name *</Label>
                <Input
                  placeholder="e.g., Vybz Entertainment or John Doe"
                  className="input-glass mt-2"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Type *</Label>
                <select
                  className="w-full h-10 mt-2 px-3 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08]"
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                >
                  <option value="individual">Individual Promoter</option>
                  <option value="company">Registered Company</option>
                  <option value="nonprofit">Non-Profit Organization</option>
                </select>
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Contact Phone *</Label>
                <Input
                  placeholder="+1 876 555 0000"
                  className="input-glass mt-2"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Business Address</Label>
                <Input
                  placeholder="Street address"
                  className="input-glass mt-2"
                  value={formData.business_address}
                  onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Parish *</Label>
                <select
                  className="w-full h-10 mt-2 px-3 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08]"
                  value={formData.parish}
                  onChange={(e) => setFormData({ ...formData, parish: e.target.value })}
                >
                  <option value="">Select parish</option>
                  <option value="Kingston">Kingston</option>
                  <option value="St. Andrew">St. Andrew</option>
                  <option value="St. Thomas">St. Thomas</option>
                  <option value="Portland">Portland</option>
                  <option value="St. Mary">St. Mary</option>
                  <option value="St. Ann">St. Ann</option>
                  <option value="Trelawny">Trelawny</option>
                  <option value="St. James">St. James</option>
                  <option value="Hanover">Hanover</option>
                  <option value="Westmoreland">Westmoreland</option>
                  <option value="St. Elizabeth">St. Elizabeth</option>
                  <option value="Manchester">Manchester</option>
                  <option value="Clarendon">Clarendon</option>
                  <option value="St. Catherine">St. Catherine</option>
                </select>
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Event Experience</Label>
                <textarea
                  placeholder="Tell us about your experience hosting events..."
                  className="w-full h-24 mt-2 px-3 py-2 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08] resize-none"
                  value={formData.experience_description}
                  onChange={(e) => setFormData({ ...formData, experience_description: e.target.value })}
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Expected Events Per Year</Label>
                <select
                  className="w-full h-10 mt-2 px-3 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08]"
                  value={formData.expected_events_per_year}
                  onChange={(e) => setFormData({ ...formData, expected_events_per_year: parseInt(e.target.value) })}
                >
                  <option value={1}>1-2 events</option>
                  <option value={5}>3-5 events</option>
                  <option value={10}>6-10 events</option>
                  <option value={20}>10+ events</option>
                </select>
              </div>
            </div>

            <Button 
              onClick={() => setStep(3)} 
              disabled={!formData.business_name || !formData.contact_phone || !formData.parish}
              className="w-full btn-primary h-12"
            >
              Continue to Verification
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 3: Document Upload */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-heading font-semibold text-xl mb-1">Verification Documents</h2>
              <p className="text-sm text-muted-foreground">
                Upload your ID and business permit to verify your identity
              </p>
            </div>

            {/* ID Upload */}
            <div className="glass rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-white">Government ID *</p>
                  <p className="text-xs text-muted-foreground">Passport or Driver's License</p>
                </div>
              </div>

              <input
                ref={idInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange(e, "id")}
                className="hidden"
              />

              {idDocument ? (
                <div className="relative">
                  {idPreview ? (
                    <img 
                      src={idPreview} 
                      alt="ID Preview" 
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-40 bg-white/5 rounded-lg flex items-center justify-center">
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => {
                        setIdDocument(null);
                        setIdPreview(null);
                      }}
                      className="p-1.5 bg-red-500/80 rounded-full"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-400">
                    <Check className="w-4 h-4" />
                    <span>{idDocument.name}</span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => idInputRef.current?.click()}
                  className="w-full h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors"
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to upload</span>
                  <span className="text-xs text-muted-foreground">JPG, PNG or PDF (max 5MB)</span>
                </button>
              )}
            </div>

            {/* Permit Upload */}
            <div className="glass rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-white">Business Permit (Optional)</p>
                  <p className="text-xs text-muted-foreground">For registered businesses</p>
                </div>
              </div>

              <input
                ref={permitInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange(e, "permit")}
                className="hidden"
              />

              {permitDocument ? (
                <div className="relative">
                  {permitPreview ? (
                    <img 
                      src={permitPreview} 
                      alt="Permit Preview" 
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-40 bg-white/5 rounded-lg flex items-center justify-center">
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => {
                        setPermitDocument(null);
                        setPermitPreview(null);
                      }}
                      className="p-1.5 bg-red-500/80 rounded-full"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-400">
                    <Check className="w-4 h-4" />
                    <span>{permitDocument.name}</span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => permitInputRef.current?.click()}
                  className="w-full h-24 border-2 border-dashed border-white/10 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-amber-500/30 transition-colors"
                >
                  <Upload className="w-6 h-6 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Click to upload (optional)</span>
                </button>
              )}
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-400">Important</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your documents will be reviewed by our team. This usually takes 1-2 business days. 
                  We'll notify you once your account is verified.
                </p>
              </div>
            </div>

            <Button 
              onClick={handleSubmit} 
              disabled={!idDocument || loading}
              className="w-full btn-primary h-12"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Submit Application"
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By submitting, you agree to our Partner Terms and verify that all information is accurate.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PartnerApply;
